<template>
  <div
    ref="container"
    class="contents"
    :class="{
      'hover:[&_*]:underline hover:[&_*]:decoration-dotted': props.decorate && (slots.content || props.content) && !props.disabled
    }"
    :aria-describedby="id"
    data-component-name="VTooltip"
    v-on="listeners"
  >
    <slot />
  </div>
  <VTeleport v-if="(slots.content || props.content) && !props.disabled && tooltipOpen" to="body">
    <div 
      :id="id"
      ref="tooltip"
      role="tooltip"
      class="modern-color-theme font-poppins text-neutral-100 fixed z-[1002] py-1 px-2 bg-neutral-750 shadow rounded-lg text-sm flex gap-3 items-center"
      :class="computedClass"
      :style="[position.style, computedStyle]"
    >
      <div>
        <slot name="content" :close="() => tooltipOpen = false">{{ props.content }}</slot>
      </div>
      <div v-if="props.dismissMode === 'manual'">
        <VButton variant="text" size="xs" @click="tooltipOpen = false">
          <VIcon size="sm" color="white" name="Solid/x-mark" />
        </VButton>
      </div>
    </div>
  </VTeleport>
</template>
<script lang="ts" setup>
import VButton from '@library/buttons/VButton.vue';
import VIcon from '@library/labels/VIcon.vue';
import VTeleport from '@library/utilities/VTeleport.vue';
import { getDOMRect, getTextRect, type Position, useAnimationFramePosition } from '@utils/position';
import type { Timeout } from '@utils/types';
import { useElementId } from '@utils/utils';
import { useWindowSize } from '@utils/window';
import { computed, ref, useTemplateRef, watchEffect } from 'vue';

defineOptions({
  name: 'VTooltip'
})

const props = withDefaults(
  defineProps<{
    /**
     * Tooltip position
     */
    position?: Position.Snap
    /**
     * Controls how tooltip is hidden
     * 
     * - `auto` - hides tooltip when mouse leaves wrapped element
     * - `custom` - hides tooltip when user clicks integrated close button
     * - `manual` - hides tooltip when exposed close function is called
     */
    dismissMode?: 'custom' | 'auto' | 'manual'
    /**
     * Starts tooltip in visible state
     */
    visible?: boolean
    /**
     * Content of the tooltip. If you need to display more complex content use the `content` slot.
     */
    content?: string | null
    /**
     * Prevents tooltip from being displayed
     */
    disabled?: boolean
    /**
     * Classes applied to the tooltip div
     */
    tooltipClass?: string
    /**
     * Decorate content with an underline when hovering
     */
    decorate?: boolean
  }>(),
  {
    position: 'bottom left',
    dismissMode: 'auto',
    content: undefined,
    disabled: false,
    visible: false,
    decorate: false,
    tooltipClass: ''
  }
)

const slots = defineSlots<{
  default(): unknown
  content?(props: { close: () => void }): unknown
}>()

const id = useElementId(undefined)

const tooltipOpen = ref(props.visible)

const tooltipElement = useTemplateRef('tooltip')
const containerElement = useTemplateRef('container')

const getPosition = (rect: DOMRect, position: Position.Snap, width: number, height: number) => {
  switch (position) {
    case 'bottom left': {
      return {
        top: rect.bottom + 4,
        left: rect.left
      }
    }
    case 'bottom': {
      return {
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2 - width / 2
      }
    }
    case 'bottom right': {
      return {
        top: rect.bottom + 4,
        left: rect.right - width
      }
    }
    case 'top left': {
      return {
        top: rect.top - height - 4,
        left: rect.left
      }
    }
    case 'top': {
      return {
        top: rect.top - height - 4,
        left: rect.left + rect.width / 2 - width / 2
      }
    }
    case 'top right': {
      return {
        top: rect.top - height - 4,
        left: rect.right - width
      }
    }
    case 'right': {
      return {
        top: rect.top + rect.height / 2 - height / 2,
        left: rect.right + 4
      }
    }
    case 'left': {
      return {
        top: rect.top + rect.height / 2 - height / 2,
        left: rect.left - width - 4
      }
    }
  }
}

const windowSize = useWindowSize()

const position = useAnimationFramePosition(
  tooltipOpen,
  () => {
    const rect = getDOMRect(containerElement.value?.children[0])

    const { width, height } = getDOMRect(tooltipElement.value, false) ?? { width: 0, height: 0 }

    const { left, top } = getPosition(rect, props.position, width, height)
    const right = left + width
    const bottom = top + height

    const { right: windowWidth, bottom: windowHeight } = windowSize.value

    const leftOverflow = left < 0
    const rightOverflow = right > windowWidth
    const topOverflow = top < 0
    const bottomOverflow = bottom > windowHeight

    if (leftOverflow || rightOverflow || topOverflow || bottomOverflow) {
      const [ verticalPosition, horizontalPosition ] = (
        props.position === 'bottom' || props.position === 'top'
      ) ? [props.position, 'middle'] : (
        props.position === 'right' || props.position === 'left'
      ) ? ['middle', props.position] : props.position.split(' ', 2)

      const verticalOverflowPosition = topOverflow || bottomOverflow ? (
        {
          top: 'bottom',
          bottom: 'top'
        }[verticalPosition] || (topOverflow ? 'bottom' : 'top')
      ) : (
        verticalPosition
      )

      const horizontalOverflowPosition = leftOverflow || rightOverflow ? (
        {
          left: 'right',
          right: 'left'
        }[horizontalPosition] || (leftOverflow ? 'left' : 'right')
      ) : (
        horizontalPosition
      )

      const pos = horizontalOverflowPosition === 'middle' ? verticalOverflowPosition : verticalOverflowPosition === 'middle' ? horizontalOverflowPosition : `${verticalOverflowPosition} ${horizontalOverflowPosition}`

      const { left, top } = getPosition(rect, pos as Position.Snap, width, height)

      return {
        position: pos,
        style: {
          left: `${left}px`,
          top: `${top}px`
        }
      }
    } else {
      return {
        position: props.position,
        style: {
          left: `${left}px`,
          top: `${top}px`
        }
      }
    }
  }
)

// Timer used to delay opening
const timer = ref<Timeout>()

const toggleLater = (shouldOpen: boolean) => {
  if (shouldOpen) {
    if (timer.value) return

    timer.value = setTimeout(() => {
      timer.value = undefined

      tooltipOpen.value = true
    }, 600)
  } else {
    clearTimeout(timer.value)

    timer.value = undefined

    tooltipOpen.value = false
  }
}

const openListeners = {
  mouseenter: () => toggleLater(true),
  focusin: () => toggleLater(true)
}

const closeListeners = {
  mouseleave: () => toggleLater(false),
  focusout: () => toggleLater(false)
}

const listeners = computed(() => {
  const closeListenersForDismissMode = props.dismissMode === 'auto' ? closeListeners : {}

  if (props.visible) {
    return closeListenersForDismissMode
  } else {
    return {
      ...openListeners,
      ...closeListenersForDismissMode
    }
  }
})

const computedClass = computed(() => {
  const pos = position.value.position

  const isBottom = pos === 'bottom'
  const isTop = pos === 'top'
  const isLeft = pos === 'left'
  const isRight = pos === 'right'

  const isBottomRight = pos === 'bottom right'
  const isBottomLeft = pos === 'bottom left'
  const isTopRight = pos === 'top right'
  const isTopLeft = pos === 'top left'

  const widthClass = !slots.content ? 'max-w-xs' : 'w-max'

  return {
    'after:content-[""] after:absolute after:border-4 after:border-transparent': true,
    'after:border-b-neutral-750 after:-top-2': isBottom || isBottomLeft || isBottomRight,
    'after:border-t-neutral-750 after:-bottom-2': isTop || isTopLeft || isTopRight,
    'after:border-r-neutral-750 after:-left-2': isRight,
    'after:border-l-neutral-750 after:-right-2': isLeft,
    'after:left-1/2 after:-translate-x-1/2': isTop || isBottom,
    'after:top-1/2 after:-translate-y-1/2': isLeft || isRight,
    'after:left-3': isBottomLeft || isTopLeft,
    'after:right-3': isBottomRight || isTopRight,
    [props.tooltipClass]: true,
    [widthClass]: true,
  }
})

/**
 * This area is needed to correctly size content passed by props
 */
const contentWidth = ref(320)

watchEffect(() => {
  if (slots.content || !props.content) return

  contentWidth.value = getTextRect(props.content, 'modern-color-theme font-poppins p-2 px-3 text-sm whitespace-nowrap').width
})

const computedStyle = computed(() => {
  if (slots.content) return {}
 
  return {
    width: `${contentWidth.value}px`
  }
})
</script>